import { useMemo } from "react";
import { REGIONS, TEAMS, getUsersByTeam, Team } from "../contexts/UserContext";

export interface TeamData {
  id: string;
  name: string;
  regionId: string;
  manager: string;
  managerId: string;
  memberCount: number;
  activeSalespeople: number;
  totalPoints: number;
  monthlyTarget: number;
  totalRevenue: number;
  revenueTarget: number;
  dealsCompleted: number;
  avgPointsPerMember: number;
  campaignParticipation: number;
  trainingCompletion: number;
  pendingApprovals: number;
  status: "exceeding" | "on_track" | "needs_attention";
}

export interface RegionData {
  id: string;
  name: string;
  managerId: string;
  teams: TeamData[];
  totalMembers: number;
  totalPoints: number;
  totalRevenue: number;
  regionalTarget: number;
  revenueTarget: number;
  attainmentPercent: number;
  revenueAttainment: number;
  avgCampaignParticipation: number;
  totalPendingApprovals: number;
  teamsExceeding: number;
  teamsNeedingAttention: number;
}

export interface OrganizationData {
  regions: RegionData[];
  allTeams: TeamData[];
  totalMembers: number;
  totalTeams: number;
  totalRegions: number;
  totalPoints: number;
  totalRevenue: number;
  totalTarget: number;
  totalRevenueTarget: number;
  attainmentPercent: number;
  revenueAttainment: number;
  avgCampaignParticipation: number;
  totalPendingApprovals: number;
  teamsExceeding: number;
  teamsNeedingAttention: number;
  pointsByActivity: { name: string; value: number; color: string }[];
  tierDistribution: { tier: string; members: number; points: number; avgPoints: number }[];
}

function generateTeamData(team: Team, teamIndex: number): TeamData {
  const members = getUsersByTeam(team.id);
  const salespeople = members.filter(u => u.role === "user");
  
  const basePoints = 150000 + (teamIndex * 20000);
  const baseRevenue = 2000000 + (teamIndex * 300000);
  const pointsVariation = ((teamIndex * 17) % 50) * 1000;
  const revenueVariation = ((teamIndex * 23) % 50) * 10000;
  const totalPoints = basePoints + pointsVariation;
  const monthlyTarget = 200000;
  
  const attainmentPct = (totalPoints / monthlyTarget) * 100;
  const status = attainmentPct >= 100 ? "exceeding" : attainmentPct < 80 ? "needs_attention" : "on_track";
  
  return {
    id: team.id,
    name: team.name,
    regionId: team.regionId,
    manager: members.find(u => u.role === "manager")?.name || "Unassigned",
    managerId: team.managerId,
    memberCount: team.memberCount,
    activeSalespeople: salespeople.length,
    totalPoints,
    monthlyTarget,
    totalRevenue: baseRevenue + revenueVariation,
    revenueTarget: 3000000,
    dealsCompleted: 80 + ((teamIndex * 7) % 40),
    avgPointsPerMember: Math.floor(totalPoints / team.memberCount),
    campaignParticipation: 75 + ((teamIndex * 3) % 20),
    trainingCompletion: 60 + ((teamIndex * 5) % 35),
    pendingApprovals: (teamIndex * 2) % 5,
    status,
  };
}

const ALL_TEAM_DATA: TeamData[] = TEAMS.map((team, index) => generateTeamData(team, index));

const ALL_REGION_DATA: RegionData[] = REGIONS.map(region => {
  const teams = ALL_TEAM_DATA.filter(t => t.regionId === region.id);
  const totalMembers = teams.reduce((sum, t) => sum + t.memberCount, 0);
  const totalPoints = teams.reduce((sum, t) => sum + t.totalPoints, 0);
  const totalRevenue = teams.reduce((sum, t) => sum + t.totalRevenue, 0);
  const regionalTarget = teams.reduce((sum, t) => sum + t.monthlyTarget, 0);
  const revenueTarget = teams.reduce((sum, t) => sum + t.revenueTarget, 0);
  const attainmentPercent = Math.round((totalPoints / regionalTarget) * 100);
  const revenueAttainment = Math.round((totalRevenue / revenueTarget) * 100);
  const avgCampaignParticipation = Math.round(teams.reduce((sum, t) => sum + t.campaignParticipation, 0) / teams.length);
  const totalPendingApprovals = teams.reduce((sum, t) => sum + t.pendingApprovals, 0);

  return {
    id: region.id,
    name: region.name,
    managerId: region.managerId,
    teams,
    totalMembers,
    totalPoints,
    totalRevenue,
    regionalTarget,
    revenueTarget,
    attainmentPercent,
    revenueAttainment,
    avgCampaignParticipation,
    totalPendingApprovals,
    teamsExceeding: teams.filter(t => t.status === "exceeding").length,
    teamsNeedingAttention: teams.filter(t => t.status === "needs_attention").length,
  };
});

const ORG_DATA: OrganizationData = (() => {
  const regions = ALL_REGION_DATA;
  const allTeams = ALL_TEAM_DATA;
  
  const totalMembers = regions.reduce((sum, r) => sum + r.totalMembers, 0);
  const totalTeams = TEAMS.length;
  const totalRegions = REGIONS.length;
  const totalPoints = regions.reduce((sum, r) => sum + r.totalPoints, 0);
  const totalRevenue = regions.reduce((sum, r) => sum + r.totalRevenue, 0);
  const totalTarget = regions.reduce((sum, r) => sum + r.regionalTarget, 0);
  const totalRevenueTarget = regions.reduce((sum, r) => sum + r.revenueTarget, 0);
  const attainmentPercent = Math.round((totalPoints / totalTarget) * 100);
  const revenueAttainment = Math.round((totalRevenue / totalRevenueTarget) * 100);
  const avgCampaignParticipation = Math.round(regions.reduce((sum, r) => sum + r.avgCampaignParticipation, 0) / regions.length);
  const totalPendingApprovals = regions.reduce((sum, r) => sum + r.totalPendingApprovals, 0);
  const teamsExceeding = regions.reduce((sum, r) => sum + r.teamsExceeding, 0);
  const teamsNeedingAttention = regions.reduce((sum, r) => sum + r.teamsNeedingAttention, 0);
  
  const pointsByActivity = [
    { name: "Campaigns", value: Math.round(totalPoints * 0.40), color: "#3b82f6" },
    { name: "Bonuses", value: Math.round(totalPoints * 0.25), color: "#10b981" },
    { name: "Milestones", value: Math.round(totalPoints * 0.20), color: "#f59e0b" },
    { name: "Spot Awards", value: Math.round(totalPoints * 0.15), color: "#8b5cf6" },
  ];
  
  const tierDistribution = [
    { tier: "Bronze", members: Math.round(totalMembers * 0.30), points: 0, avgPoints: 0 },
    { tier: "Silver", members: Math.round(totalMembers * 0.35), points: 0, avgPoints: 0 },
    { tier: "Gold", members: Math.round(totalMembers * 0.25), points: 0, avgPoints: 0 },
    { tier: "Platinum", members: Math.round(totalMembers * 0.10), points: 0, avgPoints: 0 },
  ];
  
  tierDistribution.forEach((t, i) => {
    const avgMultiplier = i === 0 ? 0.6 : i === 1 ? 0.8 : i === 2 ? 1.2 : 2.0;
    const avgPoints = Math.round((totalPoints / totalMembers) * avgMultiplier);
    t.avgPoints = avgPoints;
    t.points = avgPoints * t.members;
  });
  
  return {
    regions,
    allTeams,
    totalMembers,
    totalTeams,
    totalRegions,
    totalPoints,
    totalRevenue,
    totalTarget,
    totalRevenueTarget,
    attainmentPercent,
    revenueAttainment,
    avgCampaignParticipation,
    totalPendingApprovals,
    teamsExceeding,
    teamsNeedingAttention,
    pointsByActivity,
    tierDistribution,
  };
})();

export function useOrganizationData(): OrganizationData {
  return useMemo(() => ORG_DATA, []);
}

export function useRegionData(regionId: string): RegionData | null {
  return useMemo(() => {
    return ALL_REGION_DATA.find(r => r.id === regionId) || null;
  }, [regionId]);
}

export function useTeamData(teamId: string): TeamData | null {
  return useMemo(() => {
    return ALL_TEAM_DATA.find(t => t.id === teamId) || null;
  }, [teamId]);
}

export function getTeamDataByIndex(index: number): TeamData | null {
  return ALL_TEAM_DATA[index] || null;
}

export function getRegionTeams(regionId: string): TeamData[] {
  return ALL_TEAM_DATA.filter(t => t.regionId === regionId);
}
